import { useEffect, useMemo, useState, type FormEvent, type ReactElement } from 'react';
import type { Session } from '@supabase/supabase-js';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  CreditCard,
  LogOut,
  ReceiptText,
  ShieldCheck,
  UsersRound,
} from 'lucide-react';
import {
  type CreditPack,
  type GroupBundleView,
  getCreditPacks,
  getGroupBundle,
  giveGroupConsent,
  joinGroupByInvite,
  payGroupShare,
  startCreditTopup,
} from './api';
import { compactDateTime, formatIdr } from './format';
import { hasSupabaseConfig, supabase, userIdFromSession } from './supabase';

type RouteName = 'login' | 'credits' | 'receipt' | 'share' | 'share-confirmation';

interface RouteState {
  name: RouteName;
  code?: string;
  groupId?: string;
}

const termsVersion = 'group-share-pay-v1-2026-06-07';

function parseRoute(): RouteState {
  const path = window.location.pathname.replace(/\/+$/, '') || '/';
  const params = new URLSearchParams(window.location.search);
  if (path === '/receipt') return { name: 'receipt' };
  if (path === '/share/confirmation') return { name: 'share-confirmation' };
  if (path.startsWith('/share/')) {
    return { name: 'share', code: decodeURIComponent(path.slice('/share/'.length)) };
  }
  if (path === '/share') {
    const code = params.get('code');
    const groupId = params.get('groupId');
    return {
      name: 'share',
      ...(code ? { code } : {}),
      ...(groupId ? { groupId } : {}),
    };
  }
  if (path === '/login') return { name: 'login' };
  return { name: 'credits' };
}

function navigate(path: string): void {
  window.history.pushState({}, '', path);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function consentMemberId(member: { id?: string; memberId?: string; userId: string | null }): string | null {
  return member.memberId ?? member.id ?? member.userId;
}

export function App(): ReactElement {
  const [route, setRoute] = useState<RouteState>(() => parseRoute());
  const [session, setSession] = useState<Session | null>(null);
  const [authReady, setAuthReady] = useState(false);

  useEffect(() => {
    const onPop = (): void => setRoute(parseRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  useEffect(() => {
    if (!supabase) {
      setAuthReady(true);
      return;
    }

    void supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      setAuthReady(true);
    });

    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
    });

    return () => data.subscription.unsubscribe();
  }, []);

  const signedIn = Boolean(session);

  return (
    <main className="app-shell">
      <header className="topbar">
        <button className="brand" type="button" onClick={() => navigate('/credits')}>
          <span className="brand-mark">S</span>
          <span>
            <strong>Studika</strong>
            <small>Pembayaran siswa</small>
          </span>
        </button>
        <nav aria-label="Navigasi utama">
          <button type="button" onClick={() => navigate('/credits')}>
            <CreditCard size={18} aria-hidden="true" />
            Kredit
          </button>
          <button type="button" onClick={() => navigate('/share')}>
            <UsersRound size={18} aria-hidden="true" />
            Share-pay
          </button>
          {signedIn ? (
            <button
              type="button"
              onClick={() => {
                void supabase?.auth.signOut();
                navigate('/login');
              }}
            >
              <LogOut size={18} aria-hidden="true" />
              Keluar
            </button>
          ) : (
            <button type="button" onClick={() => navigate('/login')}>
              <ShieldCheck size={18} aria-hidden="true" />
              Masuk
            </button>
          )}
        </nav>
      </header>

      {!hasSupabaseConfig ? (
        <Notice
          tone="error"
          title="Konfigurasi Supabase belum diisi"
          body="Isi VITE_SUPABASE_URL dan VITE_SUPABASE_ANON_KEY di Cloudflare Pages atau .env lokal."
        />
      ) : null}

      {!authReady ? (
        <section className="panel hero-panel">
          <p className="eyebrow">Memuat sesi</p>
          <h1>Menyiapkan akun pembayaran kamu.</h1>
        </section>
      ) : route.name === 'login' || !signedIn ? (
        <LoginPage session={session} nextRoute={route.name === 'login' ? '/credits' : window.location.pathname + window.location.search} />
      ) : route.name === 'receipt' ? (
        <ReceiptPage />
      ) : route.name === 'share' ? (
        <SharePayPage route={route} session={session!} />
      ) : route.name === 'share-confirmation' ? (
        <ShareConfirmationPage session={session!} route={route} />
      ) : (
        <CreditsPage session={session!} />
      )}
    </main>
  );
}

function LoginPage({
  session,
  nextRoute,
}: {
  session: Session | null;
  nextRoute: string;
}): ReactElement {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (session) navigate(nextRoute);
  }, [nextRoute, session]);

  async function submit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setLoading(true);
    setError(null);
    setMessage(null);
    const result = await supabase!.auth.signInWithPassword({ email, password });
    setLoading(false);

    if (result.error) {
      setError(result.error.message);
      return;
    }

    setMessage('Masuk berhasil. Mengalihkan...');
    navigate(nextRoute);
  }

  return (
    <section className="split-layout">
      <div className="panel hero-panel">
        <p className="eyebrow">Akun siswa</p>
        <h1>Masuk untuk lanjut bayar kelas.</h1>
        <p>
          Pembayaran diproses lewat Midtrans Snap. Kredit dan status grup hanya diperbarui oleh webhook
          pembayaran setelah transaksi terkonfirmasi.
        </p>
      </div>
      <form className="panel form-panel" onSubmit={(event) => void submit(event)}>
        <label htmlFor="email">Email</label>
        <input
          id="email"
          type="email"
          autoComplete="email"
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          required
        />
        <label htmlFor="password">Kata sandi</label>
        <input
          id="password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          required
        />
        {error ? <p className="form-error" role="alert">{error}</p> : null}
        {message ? <p className="form-success" role="status">{message}</p> : null}
        <button className="primary-button" type="submit" disabled={loading}>
          {loading ? 'Memeriksa...' : 'Masuk'}
          <ArrowRight size={18} aria-hidden="true" />
        </button>
      </form>
    </section>
  );
}

function CreditsPage({ session }: { session: Session }): ReactElement {
  const [packs, setPacks] = useState<CreditPack[]>([]);
  const [loading, setLoading] = useState(true);
  const [buyingPack, setBuyingPack] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const studentId = userIdFromSession(session);

  useEffect(() => {
    let alive = true;
    void getCreditPacks()
      .then((nextPacks) => {
        if (alive) setPacks(nextPacks);
      })
      .catch((err: unknown) => {
        if (alive) setError(err instanceof Error ? err.message : 'Gagal memuat paket kredit');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  async function buy(pack: CreditPack): Promise<void> {
    if (!studentId) return;
    setBuyingPack(pack.id);
    setError(null);
    try {
      const result = await startCreditTopup(session, studentId, pack.id);
      window.location.assign(result.redirect_url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal memulai pembayaran');
      setBuyingPack(null);
    }
  }

  return (
    <section>
      <div className="panel section-head">
        <p className="eyebrow">Paket kredit</p>
        <h1>Beli kredit pelajaran</h1>
        <p>Pilih paket, lanjut ke Midtrans Snap, lalu cek status di halaman receipt setelah kembali.</p>
      </div>
      {error ? <Notice tone="error" title="Tidak bisa melanjutkan" body={error} /> : null}
      {loading ? <div className="grid"><PackSkeleton /><PackSkeleton /><PackSkeleton /></div> : null}
      {!loading ? (
        <div className="grid">
          {packs.map((pack) => (
            <article className="pack-card" key={pack.id}>
              {pack.popular ? <span className="badge">Paling dipilih</span> : null}
              <h2>{pack.credits} kredit</h2>
              <p className="price">{formatIdr(pack.price_idr)}</p>
              <p>{formatIdr(pack.price_per_credit_idr)} per kredit</p>
              <button
                className="primary-button"
                type="button"
                onClick={() => void buy(pack)}
                disabled={Boolean(buyingPack)}
              >
                {buyingPack === pack.id ? 'Membuka Snap...' : 'Beli paket'}
                <CreditCard size={18} aria-hidden="true" />
              </button>
            </article>
          ))}
        </div>
      ) : null}
    </section>
  );
}

function ReceiptPage(): ReactElement {
  const params = new URLSearchParams(window.location.search);
  const orderId = params.get('order_id') ?? params.get('orderId') ?? '-';
  const status = params.get('transaction_status') ?? params.get('status') ?? 'menunggu konfirmasi';

  return (
    <section className="panel hero-panel">
      <ReceiptText size={32} aria-hidden="true" />
      <p className="eyebrow">Receipt web</p>
      <h1>Status pembayaran: {status}</h1>
      <dl className="facts">
        <div><dt>Order ID</dt><dd>{orderId}</dd></div>
        <div><dt>Waktu cek</dt><dd>{compactDateTime(new Date())}</dd></div>
      </dl>
      <Notice
        tone="info"
        title="Kredit tidak diberikan dari halaman ini"
        body="Kredit masuk setelah webhook Midtrans mengonfirmasi pembayaran di server Studika."
      />
      <button className="primary-button" type="button" onClick={() => navigate('/credits')}>
        Kembali ke paket kredit
        <ArrowRight size={18} aria-hidden="true" />
      </button>
    </section>
  );
}

function SharePayPage({ route, session }: { route: RouteState; session: Session }): ReactElement {
  const [code, setCode] = useState(route.code ?? '');
  const [group, setGroup] = useState<GroupBundleView | null>(null);
  const [consented, setConsented] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const currentUserId = userIdFromSession(session);
  const ownSeat = useMemo(
    () => group?.members.find((member) => member.userId === currentUserId) ?? null,
    [currentUserId, group],
  );
  const memberNumber = group && ownSeat ? group.members.findIndex((member) => member.userId === ownSeat.userId) + 1 : null;

  useEffect(() => {
    if (!route.groupId) return;
    void getGroupBundle(session, route.groupId).then(setGroup).catch((err: unknown) => {
      setError(err instanceof Error ? err.message : 'Gagal memuat grup');
    });
  }, [route.groupId, session]);

  async function join(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const joined = await joinGroupByInvite(session, code.trim());
      setGroup(joined);
      navigate(`/share?groupId=${encodeURIComponent(joined.id)}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Kode undangan tidak dapat dipakai');
    } finally {
      setBusy(false);
    }
  }

  async function consentAndPay(): Promise<void> {
    if (!group || !ownSeat || !ownSeat.userId || !currentUserId) return;
    setBusy(true);
    setError(null);
    try {
      const memberId = consentMemberId(ownSeat);
      if (!memberId) {
        throw new Error('Kursi anggota tidak ditemukan. Muat ulang undangan lalu coba lagi.');
      }
      await giveGroupConsent(session, group.id, memberId, termsVersion);
      const payment = await payGroupShare(session, group.id);
      window.location.assign(payment.snapRedirectUrl);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal memulai pembayaran grup');
      setBusy(false);
    }
  }

  return (
    <section className="split-layout">
      <div className="panel hero-panel">
        <p className="eyebrow">Share-pay teman</p>
        <h1>Gabung roster, setujui syarat, lalu bayar bagian kamu.</h1>
        <p>Bundle aktif setelah semua kursi lunas dan webhook Midtrans mengonfirmasi pembayaran.</p>
      </div>

      <div className="panel form-panel">
        {!group ? (
          <form onSubmit={(event) => void join(event)}>
            <label htmlFor="invite-code">Kode undangan</label>
            <input
              id="invite-code"
              value={code}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="Contoh: AB12CD34"
              required
            />
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            <button className="primary-button" type="submit" disabled={busy}>
              {busy ? 'Mengklaim kursi...' : 'Gabung roster'}
              <UsersRound size={18} aria-hidden="true" />
            </button>
          </form>
        ) : (
          <>
            <GroupSummary group={group} memberNumber={memberNumber} />
            <div className="terms-box">
              <h2>Syarat bundle</h2>
              <p>
                Kamu membayar bagian sendiri sebesar {formatIdr(group.pricePerPersonIdr)}. Kursi tidak
                aktif sampai semua anggota membayar. Kredit diberikan oleh sistem setelah pembayaran
                terkonfirmasi, bukan oleh browser ini.
              </p>
              <label className="check-row" htmlFor="group-consent">
                <input
                  id="group-consent"
                  type="checkbox"
                  checked={consented}
                  onChange={(event) => setConsented(event.target.checked)}
                />
                Saya setuju dengan syarat bundle grup versi {termsVersion}.
              </label>
            </div>
            {error ? <p className="form-error" role="alert">{error}</p> : null}
            <button
              className="primary-button"
              type="button"
              disabled={!consented || busy || ownSeat?.paymentStatus === 'paid'}
              onClick={() => void consentAndPay()}
            >
              {busy ? 'Membuka Snap...' : ownSeat?.paymentStatus === 'paid' ? 'Bagian sudah lunas' : 'Setuju dan bayar'}
              <ShieldCheck size={18} aria-hidden="true" />
            </button>
          </>
        )}
      </div>
    </section>
  );
}

function ShareConfirmationPage({
  session,
  route,
}: {
  session: Session;
  route: RouteState;
}): ReactElement {
  const params = new URLSearchParams(window.location.search);
  const groupId = route.groupId ?? params.get('groupId');
  const orderId = params.get('order_id') ?? params.get('orderId') ?? '-';
  const status = params.get('transaction_status') ?? params.get('status') ?? 'menunggu konfirmasi';

  return (
    <section className="panel hero-panel">
      <CheckCircle2 size={34} aria-hidden="true" />
      <p className="eyebrow">Konfirmasi share-pay</p>
      <h1>Pembayaran bagian kamu sedang dicek.</h1>
      <dl className="facts">
        <div><dt>Group ID</dt><dd>{groupId ?? '-'}</dd></div>
        <div><dt>Order ID</dt><dd>{orderId}</dd></div>
        <div><dt>Status Snap</dt><dd>{status}</dd></div>
      </dl>
      <Notice
        tone="info"
        title="Aktivasi menunggu semua anggota"
        body="Bundle grup aktif setelah seluruh kursi lunas dan webhook Midtrans menandai pembayaran di server."
      />
      {groupId ? (
        <button
          className="primary-button"
          type="button"
          onClick={() => navigate(`/share?groupId=${encodeURIComponent(groupId)}`)}
        >
          Lihat roster
          <UsersRound size={18} aria-hidden="true" />
        </button>
      ) : (
        <button className="primary-button" type="button" onClick={() => navigate('/share')}>
          Masukkan kode lagi
          <ArrowRight size={18} aria-hidden="true" />
        </button>
      )}
      <input type="hidden" value={session.user.id} readOnly aria-hidden="true" />
    </section>
  );
}

function GroupSummary({
  group,
  memberNumber,
}: {
  group: GroupBundleView;
  memberNumber: number | null;
}): ReactElement {
  const paidCount = group.members.filter((member) => member.paymentStatus === 'paid').length;
  return (
    <div className="summary">
      <h2>Roster bundle</h2>
      <dl className="facts">
        <div><dt>Status</dt><dd>{group.status}</dd></div>
        <div><dt>Kursi kamu</dt><dd>{memberNumber ? `#${memberNumber}` : 'Terdaftar'}</dd></div>
        <div><dt>Harga bagian</dt><dd>{formatIdr(group.pricePerPersonIdr)}</dd></div>
        <div><dt>Lunas</dt><dd>{paidCount}/{group.sizeK}</dd></div>
      </dl>
      <ul className="roster">
        {group.members.map((member, index) => (
          <li key={`${member.userId ?? 'open'}-${index}`}>
            <span>{member.name ?? (member.userId ? 'Anggota Studika' : 'Kursi kosong')}</span>
            <strong>{member.paymentStatus === 'paid' ? 'Lunas' : 'Belum lunas'}</strong>
          </li>
        ))}
      </ul>
    </div>
  );
}

function Notice({
  tone,
  title,
  body,
}: {
  tone: 'info' | 'error';
  title: string;
  body: string;
}): ReactElement {
  return (
    <div className={`notice notice-${tone}`} role={tone === 'error' ? 'alert' : 'status'}>
      <AlertCircle size={20} aria-hidden="true" />
      <span>
        <strong>{title}</strong>
        <small>{body}</small>
      </span>
    </div>
  );
}

function PackSkeleton(): ReactElement {
  return (
    <article className="pack-card skeleton" aria-hidden="true">
      <span />
      <h2 />
      <p />
      <button type="button" disabled />
    </article>
  );
}
